import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Circle,
  FileText,
  GraduationCap,
  Images,
  ListChecks,
  Lock,
  PlayCircle,
  Trophy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCatalog, useMyProgress } from '../lib/data';
import type { Step, Track } from '../types';
import SiteHeader from './SiteHeader';
import SignIn from './SignIn';
import CandidateHome from './CandidateHome';
import TrainEnvCard from './TrainEnvCard';
import ThisWeekCard from './ThisWeekCard';
import YourPath from './YourPath';

export default function Home() {
  const { user, profile, isAuthorized, isCandidate, emailLinkState } = useAuth();

  // While a magic-link is completing (or awaiting the cross-device email), block
  // ALL gated rendering — even if a stale session is momentarily still signed in —
  // and let SignIn drive the link flow.
  if (emailLinkState === 'processing' || emailLinkState === 'needs_email') return <SignIn />;
  if (!user || isAuthorized !== true) return <SignIn />;
  // Candidates get a scoped home (granted trails via the API; no Dangal/admin/feed).
  if (isCandidate) return <CandidateHome />;

  const firstName = (profile?.displayName || '').split(' ')[0] || 'there';

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <div className="animate-rise-in">
          <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">
            Welcome back, {firstName}.
          </h1>
          <p className="mt-0.5 text-sm text-ink-500 dark:text-paper-300">
            Did your kasrat today? Pick a kushti and earn your Zor{' '}
            (<span lang="hi">ज़ोर</span>).
          </p>
        </div>

        {profile?.mentor && (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-rule bg-paper-0 px-4 py-3 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
              {profile.mentor.initials}
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <GraduationCap className="h-3.5 w-3.5 text-terracotta-500" />
                Your Ustad
              </p>
              <p className="truncate text-sm font-medium text-ink-900 dark:text-paper-50">
                {profile.mentor.displayName}
              </p>
            </div>
          </div>
        )}

        <TrainEnvCard />
        <ThisWeekCard />
        <YourPath uid={user.uid} candidate={false} />

        <section className="mt-6">
          <TrailList uid={user.uid} />
        </section>
      </main>
    </div>
  );
}

function TrackGroups({ uid, tracks }: { uid: string; tracks: Track[] }) {
  const certs = tracks.filter((t) => t.id.startsWith('track_cert_'));
  const rest = tracks.filter((t) => !t.id.startsWith('track_cert_'));
  return (
    <div className="flex flex-col gap-6">
      {certs.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            <GraduationCap className="h-3.5 w-3.5 text-terracotta-500" />
            Certification
          </div>
          <div className="flex flex-col gap-3">
            {certs.map((t) => (
              <TrackCard key={t.id} uid={uid} track={t} />
            ))}
          </div>
        </div>
      )}
      {rest.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            <Trophy className="h-3.5 w-3.5 text-terracotta-500" />
            Your kushtis
          </div>
          <div className="flex flex-col gap-3">
            {rest.map((t) => (
              <TrackCard key={t.id} uid={uid} track={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrailList({ uid }: { uid: string }) {
  const { tracks, loading } = useCatalog();
  const [activeTag, setActiveTag] = useState<string | null>(null);

  if (loading) {
    return <div className="py-10 text-center text-sm text-ink-400">Loading your kushtis…</div>;
  }
  if (tracks.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-rule p-8 text-center text-sm text-ink-400 bg-paper-100/50 dark:border-clay-rule dark:bg-clay-surface/60">
        No kushtis yet. A Khalifa will assign your first one soon.
      </div>
    );
  }

  const allTags = Array.from(new Set(tracks.flatMap((t) => t.tags ?? [])));
  const filtered = activeTag ? tracks.filter((t) => t.tags?.includes(activeTag)) : tracks;

  return (
    <div>
      {allTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveTag(null)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
              activeTag === null
                ? 'bg-terracotta-500 text-white'
                : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
            }`}
          >
            All
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
                activeTag === tag
                  ? 'bg-terracotta-500 text-white'
                  : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}
      <TrackGroups uid={uid} tracks={filtered} />
    </div>
  );
}

export function TrackCard({ uid, track }: { uid: string; track: Track }) {
  const { progress } = useMyProgress(uid, track.id);
  const total = track.stepCount || progress?.totalSteps || 0;
  // completedSteps can include steps later removed from the track; never show more than the total.
  const done = Math.min(progress?.completedSteps ?? 0, total || Infinity);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const complete = progress?.status === 'completed' || (total > 0 && done >= total);
  const started = done > 0;

  return (
    <Link
      to={`/track/${track.id}`}
      className="group rounded-xl border border-rule bg-paper-0 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-terracotta-200 hover:shadow dark:border-clay-rule dark:bg-clay-surface dark:hover:border-terracotta-700"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {complete ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-verdant-500 dark:text-verdant-200" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 text-ink-300" />
            )}
            <h3 className="truncate font-semibold text-ink-900 dark:text-paper-50">{track.title}</h3>
            {track.internal && (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold text-ink-500 dark:bg-clay-raised dark:text-paper-300">
                <Lock className="h-3 w-3" />
                Internal
              </span>
            )}
          </div>
          {track.description && (
            <p className="mt-1 line-clamp-2 text-sm text-ink-500 dark:text-paper-300">
              {track.description}
            </p>
          )}
          {track.tags && track.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {track.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-paper-100 px-2 py-0.5 text-[10px] font-medium text-ink-400 dark:bg-clay-raised dark:text-paper-300"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-ink-300 transition group-hover:translate-x-0.5 group-hover:text-terracotta-500" />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-paper-200 dark:bg-clay-raised">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              complete ? 'bg-verdant-500' : 'bg-terracotta-500'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="shrink-0 text-xs font-medium text-ink-400">
          {complete ? 'Complete' : started ? `${done}/${total}` : `${total} paintras`}
        </span>
      </div>

      <ContentIndicators steps={track.steps} />
    </Link>
  );
}

const STEP_TYPE_ICONS: Record<string, LucideIcon> = {
  video: PlayCircle,
  quiz: ListChecks,
  reading: BookOpen,
  external: FileText,
  slides: Images,
};

function ContentIndicators({ steps }: { steps?: Step[] }) {
  if (!steps?.length) return null;
  const counts: Record<string, number> = {};
  for (const s of steps) {
    counts[s.type] = (counts[s.type] ?? 0) + 1;
  }
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (!entries.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2.5">
      {entries.map(([type, count]) => {
        const Icon = STEP_TYPE_ICONS[type] ?? FileText;
        return (
          <span key={type} className="flex items-center gap-1 text-xs text-ink-400">
            <Icon className="h-3.5 w-3.5" />
            {count}
          </span>
        );
      })}
    </div>
  );
}
